import { useState, useEffect, useRef } from "react";

// ─── Projects (from resume) ───────────────────────────────────
const PROJECTS = [
  {
    title: "SmartHire — AI Recruiting Platform",
    desc: "Final year project. An AI recruiting system where AI conducts interviews, evaluates candidates with LLM-powered scoring, and generates structured feedback — reducing hiring cycle time by 50%. Includes an admin panel for jobs, candidates, reports, subscriptions and analytics with Stripe billing and Calendly AI scheduling. Processed 500+ candidate evaluations in the pilot phase.",
    tags: ["Next.js", "LangChain", "Django REST", "Stripe", "Docker"],
    link: "https://github.com/Sheesikram",
    linkLabel: "VIEW ON GITHUB →",
    icon: "🤖",
    color: "#00f5ff",
  },
  {
    title: "Self Emoji — AI Content Generation",
    desc: "End-to-end AI generation platform orchestrating Gemini (images), Kling 3 (video) and Veo 3 (premium video) with provider abstraction, retry logic and GPU resource management — 95% success rate under concurrent load. Native Android app in Kotlin/Jetpack Compose with real-time status, push notifications and in-app purchases. Async pipeline via FastAPI + Celery, assets delivered through Firebase signed URLs.",
    tags: ["Kotlin", "FastAPI", "Celery", "Redis", "Stable Diffusion"],
    link: "",
    linkLabel: "PRIVATE PROJECT",
    icon: "✨",
    color: "#a855f7",
  },
  {
    title: "Recepio — AI Voice Receptionist (SaaS)",
    desc: "A generic AI-powered voice receptionist SaaS letting companies deploy their own AI phone agent with a dedicated number, an MCP server with RAG for context-aware responses. FastAPI + PostgreSQL backend for structured storage, AWS S3 for secure file management, and a responsive React dashboard.",
    tags: ["FastAPI", "React", "PostgreSQL", "Vapi", "MCP", "RAG"],
    link: "https://sheesikram.com",
    linkLabel: "VISIT WEBSITE →",
    icon: "📞",
    color: "#10b981",
  },
  {
    title: "Migoyugo — Multiplayer + AI Strategy Game",
    desc: "A fully functional real-time multiplayer strategy game supporting PvP and Human-vs-AI battles using Firebase and WebSockets, with a custom game engine and AI agents for synchronized gameplay — independently owned from design to production launch.",
    tags: ["Firebase", "WebSockets", "Game Dev", "AI Agents"],
    link: "https://www.migoyugo.com/",
    linkLabel: "PLAY NOW →",
    icon: "🎮",
    color: "#f59e0b",
  },
  {
    title: "Dental AI WhatsApp Agent",
    desc: "An AI-powered WhatsApp agent for dental clinics automating appointment booking, patient queries and follow-up workflows — handling 200+ patient interactions daily. Integrated Stripe for secure in-chat payments plus automated prompts and clinic workflow logic.",
    tags: ["Node.js", "WhatsApp Cloud API", "Stripe", "AI Automation"],
    link: "https://github.com/Sheesikram",
    linkLabel: "VIEW ON GITHUB →",
    icon: "🦷",
    color: "#ec4899",
  },
  {
    title: "SEO Command Center — Automation",
    desc: "An end-to-end SEO automation system using n8n and Airtable, automating keyword research, content briefs and rank tracking for a category-leadership strategy. Integrated Google Search Console and Google Sheets for real-time performance monitoring and automated reporting.",
    tags: ["n8n", "Airtable", "Google Search Console", "AI Automation"],
    link: "",
    linkLabel: "CASE STUDY",
    icon: "📈",
    color: "#6366f1",
  },
];

// ─── Skill proficiency bars ───────────────────────────────────
const SKILLS = [
  { name: "LangChain / LangGraph", pct: 95, color: "#00f5ff" },
  { name: "AI Agents & Automation (n8n, MCP)", pct: 95, color: "#10b981" },
  { name: "Python & FastAPI", pct: 92, color: "#a855f7" },
  { name: "RAG & Vector Databases", pct: 90, color: "#f59e0b" },
  { name: "Full-Stack (Next.js / React)", pct: 86, color: "#ec4899" },
  { name: "Mobile (Kotlin / React Native)", pct: 82, color: "#6366f1" },
];

// ─── Categorized tech stack ───────────────────────────────────
const STACK = [
  { group: "Languages", items: ["Python", "TypeScript", "JavaScript", "Kotlin", "C++"] },
  { group: "AI / ML", items: ["LangChain", "LangGraph", "CrewAI", "RAG", "OpenAI", "FAISS", "Whisper", "Transformers", "Langflow"] },
  { group: "Backend", items: ["FastAPI", "Node.js", "Django REST", "Redis", "WebSockets", "PostgreSQL"] },
  { group: "Frontend & Mobile", items: ["Next.js", "React", "React Native", "Jetpack Compose", "Streamlit"] },
  { group: "Automation", items: ["n8n", "Zapier", "Vapi", "MCP", "Webhooks"] },
  { group: "DevOps & Tools", items: ["Git", "Docker", "AWS", "Vercel", "Render", "Firebase", "Postman", "Cursor"] },
];

const EXPERIENCE = [
  {
    period: "Sep 2025 – Present",
    role: "AI Engineer",
    company: "CoreX Tech · Onsite",
    color: "#00f5ff",
    points: [
      "Architected and deployed AI-driven systems, automation pipelines and LLM-powered workflows using LangChain, LangGraph and n8n — reducing manual processing time by 60%.",
      "Built agentic workflows and domain-specific AI assistants for internal and client applications, increasing operational efficiency by 45%.",
      "Engineered AI modules including a dental AI agent (n8n) and an AI voice receptionist (Vapi) handling 1,000+ daily interactions, cutting front-desk workload by 70%.",
      "Developed Migoyugo, a real-time multiplayer game (frontend, backend, Firebase), owning the full lifecycle from design to production launch.",
    ],
  },
  {
    period: "Jan 2026 – Apr 2026",
    role: "Software Engineer",
    company: "Aspose · Remote",
    color: "#a855f7",
    points: [
      "Designed an AI-powered Retrieval & Planning System for Aspose APIs — intelligent SDK discovery via semantic search, retrieval pipelines and planner-based task decomposition.",
      "Built a production RAG platform on Weaviate with embedding models and metadata-driven indexing across thousands of API examples, docs, classes, methods and namespaces.",
      "Developed MCP-compatible retrieval services and agent tooling in FastAPI/Python, letting AI assistants search and reason over Aspose APIs through structured tool-calling.",
      "Created planner workflows generating implementation guidance for PDF, Word, Excel, Slides, Email and Imaging document-processing tasks.",
    ],
  },
  {
    period: "2025",
    role: "Generative AI Intern",
    company: "Prodigy InfoTech",
    color: "#10b981",
    points: [
      "Designed and deployed scalable APIs with FastAPI for automation pipelines.",
      "Built AI-powered solutions combining LangChain, Langflow and custom ML models.",
    ],
  },
  {
    period: "2024",
    role: "NLP Intern",
    company: "CodexCue",
    color: "#f59e0b",
    points: [
      "Developed NLP models for text summarization and semantic search using Python & Hugging Face.",
      "Built time-series forecasting models to predict sales, achieving 92% accuracy.",
    ],
  },
];

const EDUCATION = [
  {
    period: "2021 – 2025",
    degree: "Bachelor of Computer Science",
    institute: "FAST — National University of Computer and Emerging Sciences",
    detail: "Generative AI · NLP · Cloud Computing · Data Structures · OOP · Databases · Software Engineering",
    color: "#00f5ff",
  },
  {
    period: "2019 – 2021",
    degree: "ICS — Intermediate of Computer Science",
    institute: "Punjab College",
    detail: "Grade: A+",
    color: "#a855f7",
  },
];

const CV_LINK = "/Shees-Ikram-CV.pdf";

// ─── Floating particles ───────────────────────────────────────
function Particles() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let W = (canvas.width = window.innerWidth);
    let H = (canvas.height = window.innerHeight);
    const particles = Array.from({ length: 80 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      r: Math.random() * 1.5 + 0.3,
      vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
      o: Math.random(),
    }));
    const resize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    window.addEventListener("resize", resize);
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,245,255,${p.o * 0.6})`;
        ctx.fill();
      });
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0,245,255,${0.12 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }} />;
}

// ─── Animated counter ─────────────────────────────────────────
export function Counter({ target, suffix = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    let raf;
    const obs = new IntersectionObserver(([e]) => {
      // Only ever run once: re-entering the viewport must not start a second
      // animation racing the first.
      if (!e.isIntersecting || started.current) return;
      started.current = true;
      const DURATION = 1400;
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min((now - t0) / DURATION, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        setVal(Math.round(target * eased));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => { obs.disconnect(); cancelAnimationFrame(raf); };
  }, [target]);
  return <span ref={ref}>{val}{suffix}</span>;
}

// ─── Skill bar ────────────────────────────────────────────────
function SkillBar({ name, pct, color, delay = 0 }) {
  const [width, setWidth] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setTimeout(() => setWidth(pct), delay);
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [pct, delay]);
  return (
    <div ref={ref} style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13, fontFamily: "'Rajdhani', sans-serif", letterSpacing: 1 }}>
        <span style={{ color: "#c0cfe0" }}>{name}</span>
        <span style={{ color }}>{pct}%</span>
      </div>
      <div style={{ height: 6, background: "rgba(255,255,255,0.07)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${width}%`,
          background: `linear-gradient(90deg, ${color}88, ${color})`,
          borderRadius: 3, transition: "width 1.2s cubic-bezier(0.4,0,0.2,1)",
          boxShadow: `0 0 12px ${color}88`,
        }} />
      </div>
    </div>
  );
}

// ─── Project card with 3D flip ─────────────────────────────────
function ProjectCard({ p }) {
  const [flipped, setFlipped] = useState(false);
  const hasLink = Boolean(p.link);
  return (
    <div
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
      style={{ perspective: 1000, height: 250, cursor: "pointer" }}
    >
      <div style={{
        position: "relative", width: "100%", height: "100%",
        transformStyle: "preserve-3d",
        transition: "transform 0.7s cubic-bezier(0.4,0,0.2,1)",
        transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
      }}>
        {/* Front */}
        <div style={{
          position: "absolute", inset: 0, backfaceVisibility: "hidden",
          background: "linear-gradient(135deg, rgba(15,20,40,0.95), rgba(5,10,25,0.9))",
          border: `1px solid ${p.color}33`, borderRadius: 16, padding: 24,
          display: "flex", flexDirection: "column", justifyContent: "space-between",
          boxShadow: `0 0 30px ${p.color}18`,
        }}>
          <div>
            <div style={{ fontSize: 36, marginBottom: 10 }}>{p.icon}</div>
            <h3 style={{ color: "#fff", fontSize: 18, fontFamily: "'Orbitron', sans-serif", marginBottom: 8, lineHeight: 1.3 }}>{p.title}</h3>
            <p style={{ color: "#8899aa", fontSize: 13, lineHeight: 1.6, fontFamily: "'Rajdhani', sans-serif" }}>{p.desc.slice(0, 88)}…</p>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {p.tags.slice(0, 3).map(t => (
              <span key={t} style={{ fontSize: 10, padding: "3px 8px", border: `1px solid ${p.color}66`, borderRadius: 20, color: p.color, fontFamily: "'Rajdhani', sans-serif", letterSpacing: 0.5 }}>{t}</span>
            ))}
          </div>
        </div>
        {/* Back */}
        <div style={{
          position: "absolute", inset: 0, backfaceVisibility: "hidden",
          transform: "rotateY(180deg)",
          background: `linear-gradient(135deg, ${p.color}22, ${p.color}08)`,
          border: `1px solid ${p.color}66`, borderRadius: 16, padding: 24,
          display: "flex", flexDirection: "column", justifyContent: "space-between",
          boxShadow: `0 0 40px ${p.color}30`,
        }}>
          <div style={{ overflow: "auto" }}>
            <p style={{ color: "#ccd6e0", fontSize: 12.5, lineHeight: 1.65, fontFamily: "'Rajdhani', sans-serif" }}>{p.desc}</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {p.tags.map(t => (
                <span key={t} style={{ fontSize: 10, padding: "3px 8px", background: `${p.color}22`, border: `1px solid ${p.color}88`, borderRadius: 20, color: p.color, fontFamily: "'Rajdhani', sans-serif" }}>{t}</span>
              ))}
            </div>
            {hasLink ? (
              <a href={p.link} target="_blank" rel="noreferrer" style={{
                textAlign: "center", padding: "8px 16px", background: p.color, color: "#000", borderRadius: 8,
                fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, textDecoration: "none", letterSpacing: 1,
              }}>{p.linkLabel}</a>
            ) : (
              <div style={{
                textAlign: "center", padding: "8px 16px", background: `${p.color}18`, color: p.color, borderRadius: 8,
                border: `1px solid ${p.color}55`,
                fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: 1,
              }}>{p.linkLabel}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Section fade-in wrapper ───────────────────────────────────
function FadeIn({ children, delay = 0 }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{
      opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(40px)",
      transition: `opacity 0.8s ease ${delay}ms, transform 0.8s ease ${delay}ms`,
    }}>
      {children}
    </div>
  );
}

// ─── 3D Rotating Cube ──────────────────────────────────────────
function RotatingCube() {
  return (
    <div style={{ width: 120, height: 120, perspective: 600, margin: "0 auto" }}>
      <div style={{
        width: "100%", height: "100%", position: "relative",
        transformStyle: "preserve-3d",
        animation: "rotateCube 8s linear infinite",
      }}>
        {[
          { transform: "rotateY(0deg) translateZ(60px)", bg: "rgba(0,245,255,0.12)" },
          { transform: "rotateY(180deg) translateZ(60px)", bg: "rgba(168,85,247,0.12)" },
          { transform: "rotateY(90deg) translateZ(60px)", bg: "rgba(0,245,255,0.08)" },
          { transform: "rotateY(-90deg) translateZ(60px)", bg: "rgba(168,85,247,0.08)" },
          { transform: "rotateX(90deg) translateZ(60px)", bg: "rgba(16,185,129,0.08)" },
          { transform: "rotateX(-90deg) translateZ(60px)", bg: "rgba(245,158,11,0.08)" },
        ].map((face, i) => (
          <div key={i} style={{
            position: "absolute", inset: 0,
            transform: face.transform,
            background: face.bg,
            border: "1px solid rgba(0,245,255,0.3)",
            borderRadius: 4,
          }} />
        ))}
      </div>
    </div>
  );
}

// ─── Rotating role typewriter ─────────────────────────────────
const ROLES = ["AI Full Stack Developer", "LLM & Agent Engineer", "Automation Architect", "RAG / MCP Specialist"];

function RoleRotator() {
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [del, setDel] = useState(false);
  useEffect(() => {
    const full = ROLES[idx];
    let timeout;
    if (!del && text.length < full.length) {
      timeout = setTimeout(() => setText(full.slice(0, text.length + 1)), 70);
    } else if (!del && text.length === full.length) {
      timeout = setTimeout(() => setDel(true), 1600);
    } else if (del && text.length > 0) {
      timeout = setTimeout(() => setText(full.slice(0, text.length - 1)), 35);
    } else {
      setDel(false);
      setIdx((idx + 1) % ROLES.length);
    }
    return () => clearTimeout(timeout);
  }, [text, del, idx]);
  return (
    <span style={{ color: "#00f5ff" }}>
      {text}
      <span style={{ animation: "blink 1s step-end infinite", color: "#a855f7" }}>|</span>
    </span>
  );
}

// ─── Main Portfolio ───────────────────────────────────────────
export default function Portfolio() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    document.title = "Shees Ikram — AI Full Stack Developer";
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
    setActiveSection(id);
  };

  const NAV = ["home", "about", "skills", "experience", "projects", "contact"];
  const MARQUEE = ["Python", "LangChain", "LangGraph", "FastAPI", "Next.js", "React Native", "n8n", "MCP", "RAG", "Kotlin", "Weaviate", "Vapi", "CrewAI", "Docker", "AWS", "PostgreSQL"];

  return (
    <div style={{ background: "#03040d", color: "#e2e8f0", fontFamily: "'Rajdhani', sans-serif", overflowX: "hidden", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Rajdhani:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #03040d; }
        ::-webkit-scrollbar-thumb { background: #00f5ff44; border-radius: 2px; }
        @keyframes rotateCube { from { transform: rotateX(-20deg) rotateY(0deg); } to { transform: rotateX(-20deg) rotateY(360deg); } }
        @keyframes float { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-18px); } }
        @keyframes pulse { 0%,100% { opacity: 0.5; transform: scale(1); } 50% { opacity: 1; transform: scale(1.05); } }
        @keyframes glitch { 0%,100% { text-shadow: 0 0 20px #00f5ff, 0 0 40px #00f5ff88; transform: none; } 25% { text-shadow: -2px 0 #a855f7, 2px 0 #00f5ff; transform: skewX(-0.5deg); } 50% { text-shadow: 2px 0 #a855f7, -2px 0 #00f5ff; transform: skewX(0.5deg); } 75% { text-shadow: 0 0 20px #a855f7; transform: none; } }
        @keyframes orb { 0%,100% { transform: scale(1) rotate(0deg); opacity: 0.6; } 50% { transform: scale(1.2) rotate(180deg); opacity: 0.9; } }
        @keyframes blink { 0%,100% { opacity: 1; } 50% { opacity: 0; } }
        @keyframes slideRight { from { transform: translateX(-60px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .nav-link { color: #8899aa; font-family: 'Orbitron', sans-serif; font-size: 11px; letter-spacing: 2px; text-transform: uppercase; cursor: pointer; padding: 6px 2px; transition: color 0.3s; position: relative; background: none; border: none; }
        .nav-link:hover, .nav-link.active { color: #00f5ff; }
        .nav-link::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 1px; background: #00f5ff; transition: width 0.3s; }
        .nav-link:hover::after, .nav-link.active::after { width: 100%; }
        .section-title { font-family: 'Orbitron', sans-serif; font-size: clamp(24px, 4vw, 42px); font-weight: 900; background: linear-gradient(135deg, #00f5ff, #a855f7); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; margin-bottom: 8px; }
        .section-sub { color: #4a6070; font-size: 13px; letter-spacing: 3px; text-transform: uppercase; margin-bottom: 48px; font-family: 'Orbitron', sans-serif; }
        .glow-btn { font-family: 'Orbitron', sans-serif; font-size: 12px; letter-spacing: 2px; cursor: pointer; border: none; border-radius: 4px; padding: 13px 28px; font-weight: 700; transition: all 0.3s; text-decoration: none; display: inline-block; }
        .glow-btn:hover { transform: translateY(-2px); }
        .card-hover { transition: transform 0.3s, box-shadow 0.3s; }
        .card-hover:hover { transform: translateY(-6px); }
        @media (max-width: 768px) { .hide-mobile { display: none !important; } .show-mobile { display: flex !important; } }
        @media (min-width: 769px) { .show-mobile { display: none !important; } }
      `}</style>

      <Particles />

      {/* Ambient orbs */}
      <div style={{ position: "fixed", top: "10%", left: "5%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,245,255,0.06) 0%, transparent 70%)", animation: "orb 10s ease-in-out infinite", zIndex: 0, pointerEvents: "none" }} />
      <div style={{ position: "fixed", bottom: "10%", right: "5%", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(168,85,247,0.06) 0%, transparent 70%)", animation: "orb 13s ease-in-out infinite reverse", zIndex: 0, pointerEvents: "none" }} />

      {/* ── Navbar ── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
        padding: "0 40px", height: 64,
        background: scrollY > 60 ? "rgba(3,4,13,0.92)" : "transparent",
        backdropFilter: scrollY > 60 ? "blur(20px)" : "none",
        borderBottom: scrollY > 60 ? "1px solid rgba(0,245,255,0.1)" : "none",
        transition: "all 0.4s",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div onClick={() => scrollTo("home")} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 18, fontWeight: 900, color: "#00f5ff", cursor: "pointer", letterSpacing: 2, textShadow: "0 0 20px #00f5ff88" }}>
          SI<span style={{ color: "#a855f7" }}>.</span>
        </div>
        <div className="hide-mobile" style={{ display: "flex", gap: 32 }}>
          {NAV.map(n => <button key={n} className={`nav-link ${activeSection === n ? "active" : ""}`} onClick={() => scrollTo(n)}>{n}</button>)}
        </div>
        <button className="show-mobile" onClick={() => setMenuOpen(!menuOpen)} style={{ background: "none", border: "1px solid rgba(0,245,255,0.3)", borderRadius: 6, padding: "6px 10px", color: "#00f5ff", cursor: "pointer", fontSize: 18 }}>☰</button>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(3,4,13,0.97)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 32 }}>
          <button onClick={() => setMenuOpen(false)} style={{ position: "absolute", top: 20, right: 20, background: "none", border: "none", color: "#00f5ff", fontSize: 24, cursor: "pointer" }}>✕</button>
          {NAV.map(n => <button key={n} className="nav-link" style={{ fontSize: 16 }} onClick={() => scrollTo(n)}>{n}</button>)}
        </div>
      )}

      {/* ── Hero ── */}
      <section id="home" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", zIndex: 1, padding: "80px 24px 40px" }}>
        <div style={{ maxWidth: 1100, width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 48, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 400px" }}>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 12, letterSpacing: 4, color: "#00f5ff", marginBottom: 16, animation: "slideRight 1s ease forwards" }}>
              ◈ AI FULL STACK DEVELOPER
            </div>
            <h1 style={{
              fontFamily: "'Orbitron', sans-serif", fontWeight: 900,
              fontSize: "clamp(36px, 7vw, 72px)", lineHeight: 1.1, marginBottom: 16,
              animation: "glitch 4s ease-in-out infinite",
            }}>
              <span style={{ color: "#fff" }}>SHEES</span><br />
              <span style={{ background: "linear-gradient(135deg, #00f5ff, #a855f7)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>IKRAM</span>
            </h1>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: "clamp(14px,2.4vw,20px)", marginBottom: 20, minHeight: 28 }}>
              <RoleRotator />
            </div>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: "clamp(14px, 2.5vw, 18px)", color: "#8899aa", marginBottom: 32, lineHeight: 1.7, maxWidth: 500 }}>
              I ship production AI systems across the whole stack — from native <span style={{ color: "#00f5ff" }}>mobile apps</span> (React Native, Kotlin) to <span style={{ color: "#a855f7" }}>Python AI/ML pipelines</span> and FastAPI microservices. I build LLM-powered agents, generative AI platforms, RAG systems and voice/chat automation that serve real users from <span style={{ color: "#10b981" }}>Faisalabad, Pakistan</span>.
            </div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <button className="glow-btn" onClick={() => scrollTo("projects")} style={{ background: "linear-gradient(135deg, #00f5ff, #0891b2)", color: "#000", boxShadow: "0 0 30px #00f5ff44" }}>
                VIEW PROJECTS →
              </button>
              <a href={CV_LINK} download="Shees-Ikram-CV.pdf" className="glow-btn" style={{ background: "transparent", color: "#a855f7", border: "1px solid #a855f766", boxShadow: "0 0 20px #a855f722" }}>
                DOWNLOAD CV
              </a>
            </div>
            <div style={{ display: "flex", gap: 20, marginTop: 40 }}>
              {[
                { label: "GitHub", href: "https://github.com/Sheesikram", icon: "⌥" },
                { label: "LinkedIn", href: "https://www.linkedin.com/in/shees-ikram/", icon: "◎" },
                { label: "Website", href: "https://sheesikram.com", icon: "◈" },
                { label: "Email", href: "mailto:sheesikram20@gmail.com", icon: "✉" },
              ].map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noreferrer" title={s.label} style={{ width: 40, height: 40, borderRadius: 8, border: "1px solid rgba(0,245,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", color: "#00f5ff", fontSize: 16, textDecoration: "none", transition: "all 0.3s", background: "rgba(0,245,255,0.05)" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(0,245,255,0.15)"; e.currentTarget.style.borderColor = "#00f5ff"; e.currentTarget.style.boxShadow = "0 0 15px #00f5ff44"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(0,245,255,0.05)"; e.currentTarget.style.borderColor = "rgba(0,245,255,0.2)"; e.currentTarget.style.boxShadow = "none"; }}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* 3D avatar side */}
          <div style={{ flex: "1 1 300px", display: "flex", flexDirection: "column", alignItems: "center", gap: 24 }}>
            <div style={{ animation: "float 5s ease-in-out infinite", position: "relative" }}>
              <div style={{ position: "absolute", inset: -6, borderRadius: "50%", background: "conic-gradient(#00f5ff, #a855f7, #10b981, #f59e0b, #00f5ff)", animation: "orb 3s linear infinite", opacity: 0.6 }} />
              <div style={{ position: "absolute", inset: -3, borderRadius: "50%", background: "#03040d" }} />
              <img
                src="https://avatars.githubusercontent.com/u/83660500?v=4"
                alt="Shees Ikram"
                style={{ width: "clamp(160px,25vw,220px)", height: "clamp(160px,25vw,220px)", borderRadius: "50%", position: "relative", objectFit: "cover", border: "2px solid rgba(0,245,255,0.4)", boxShadow: "0 0 60px rgba(0,245,255,0.3), 0 0 120px rgba(168,85,247,0.15)" }}
              />
            </div>
            <RotatingCube />
            <div style={{ display: "flex", gap: 24, fontFamily: "'Orbitron', sans-serif" }}>
              {[["4+", "Roles"], ["6+", "Products"], ["FAST", "CS Grad"]].map(([v, l]) => (
                <div key={l} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "clamp(18px,3vw,24px)", fontWeight: 900, color: "#00f5ff" }}>{v}</div>
                  <div style={{ fontSize: 9, color: "#4a6070", letterSpacing: 1, marginTop: 2 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div style={{ position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, animation: "float 2s ease-in-out infinite" }}>
          <div style={{ width: 1, height: 50, background: "linear-gradient(to bottom, transparent, #00f5ff)" }} />
          <div style={{ fontSize: 9, letterSpacing: 3, color: "#4a6070", fontFamily: "'Orbitron', sans-serif" }}>SCROLL</div>
        </div>
      </section>

      {/* ── Tech marquee ── */}
      <div style={{ position: "relative", zIndex: 1, overflow: "hidden", borderTop: "1px solid rgba(0,245,255,0.08)", borderBottom: "1px solid rgba(0,245,255,0.08)", background: "rgba(0,245,255,0.02)", padding: "14px 0" }}>
        <div style={{ display: "flex", width: "max-content", animation: "marquee 30s linear infinite" }}>
          {[...MARQUEE, ...MARQUEE].map((t, i) => (
            <span key={i} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 2, color: "#4a6070", padding: "0 28px", whiteSpace: "nowrap" }}>
              <span style={{ color: "#00f5ff", marginRight: 28 }}>◆</span>{t}
            </span>
          ))}
        </div>
      </div>

      {/* ── Impact Stats Banner ── */}
      <section style={{ position: "relative", zIndex: 1, padding: "48px 24px", borderBottom: "1px solid rgba(0,245,255,0.08)" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 24 }}>
          {[
            { value: 60, suffix: "%", label: "Less Manual Processing" },
            { value: 70, suffix: "%", label: "Front-Desk Workload Cut" },
            { value: 95, suffix: "%", label: "AI Pipeline Success Rate" },
            { value: 1000, suffix: "+", label: "Daily AI Interactions" },
          ].map(s => (
            <div key={s.label} style={{ textAlign: "center", padding: 24 }}>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: "clamp(28px,4vw,42px)", fontWeight: 900, color: "#00f5ff", textShadow: "0 0 20px #00f5ff66" }}>
                <Counter target={s.value} suffix={s.suffix} />
              </div>
              <div style={{ fontSize: 12, color: "#4a6070", letterSpacing: 2, marginTop: 6, fontFamily: "'Orbitron', sans-serif" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── About ── */}
      <section id="about" style={{ position: "relative", zIndex: 1, padding: "100px 24px", maxWidth: 1100, margin: "0 auto" }}>
        <FadeIn>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <h2 className="section-title">About Me</h2>
            <p className="section-sub">◈ Who I Am ◈</p>
          </div>
        </FadeIn>
        <div style={{ display: "flex", gap: 60, flexWrap: "wrap", alignItems: "flex-start" }}>
          <FadeIn delay={100}>
            <div style={{ flex: "1 1 300px" }}>
              <div style={{ background: "linear-gradient(135deg, rgba(0,245,255,0.08), rgba(168,85,247,0.08))", border: "1px solid rgba(0,245,255,0.15)", borderRadius: 20, padding: 32 }}>
                <p style={{ color: "#c0cfe0", lineHeight: 1.9, fontSize: 15, marginBottom: 20 }}>
                  I'm an <span style={{ color: "#00f5ff" }}>AI Full Stack Developer</span> with hands-on experience shipping production AI systems end-to-end — building <span style={{ color: "#a855f7" }}>LLM-powered agents</span>, generative AI content platforms, RAG systems and voice/chat automation that serve real users.
                </p>
                <p style={{ color: "#8899aa", lineHeight: 1.8, fontSize: 14 }}>
                  My stack spans native mobile apps (React Native, Kotlin), Python AI/ML pipelines and FastAPI microservices. I care about designing scalable, AI-driven products that bridge cutting-edge research with real business impact — from planner-based retrieval systems to multi-agent orchestration.
                </p>
              </div>
            </div>
          </FadeIn>
          <FadeIn delay={200}>
            <div style={{ flex: "1 1 300px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {[
                  ["Profile", "AI Full Stack Developer"],
                  ["Focus", "LLM Agents & Automation"],
                  ["Education", "BS Computer Science"],
                  ["Languages", "English, Urdu, Hindi"],
                  ["Location", "Faisalabad, Pakistan"],
                  ["Status", "Open to Opportunities"],
                ].map(([k, v]) => (
                  <div key={k} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: 16 }}>
                    <div style={{ fontSize: 10, color: "#00f5ff", letterSpacing: 2, marginBottom: 6, fontFamily: "'Orbitron', sans-serif" }}>{k}</div>
                    <div style={{ fontSize: 13, color: "#c0cfe0", fontFamily: "'Rajdhani', sans-serif", fontWeight: 600 }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 20, padding: 16, background: "rgba(0,245,255,0.05)", border: "1px solid rgba(0,245,255,0.15)", borderRadius: 12 }}>
                <div style={{ fontSize: 10, color: "#00f5ff", letterSpacing: 2, marginBottom: 8, fontFamily: "'Orbitron', sans-serif" }}>INTERESTS</div>
                <div style={{ fontSize: 13, color: "#8899aa" }}>AI Research · Open Source · Generative Media · Video Games · Travel Photography</div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── Skills ── */}
      <section id="skills" style={{ position: "relative", zIndex: 1, padding: "100px 24px", background: "rgba(0,0,0,0.3)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <FadeIn>
            <div style={{ textAlign: "center", marginBottom: 64 }}>
              <h2 className="section-title">Skills & Tech</h2>
              <p className="section-sub">◈ What I Work With ◈</p>
            </div>
          </FadeIn>
          <div style={{ display: "flex", gap: 60, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ flex: "1 1 360px" }}>
              <FadeIn delay={100}>
                <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 2, color: "#4a6070", marginBottom: 28 }}>CORE PROFICIENCY</h3>
                {SKILLS.map((s, i) => <SkillBar key={s.name} {...s} delay={i * 100} />)}
              </FadeIn>
            </div>
            <div style={{ flex: "1 1 340px" }}>
              <FadeIn delay={200}>
                <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 2, color: "#4a6070", marginBottom: 28 }}>TECH STACK</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                  {STACK.map((cat, ci) => {
                    const c = ["#00f5ff", "#a855f7", "#10b981", "#f59e0b", "#ec4899", "#6366f1"][ci % 6];
                    return (
                      <div key={cat.group}>
                        <div style={{ fontSize: 10, color: c, letterSpacing: 2, marginBottom: 8, fontFamily: "'Orbitron', sans-serif" }}>{cat.group}</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {cat.items.map(t => (
                            <span key={t} style={{
                              padding: "6px 12px", fontSize: 12, borderRadius: 6,
                              border: `1px solid ${c}44`, color: c, background: `${c}0d`,
                              fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, letterSpacing: 0.5,
                              transition: "all 0.2s", cursor: "default",
                            }}
                              onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 0 14px ${c}44`; e.currentTarget.style.transform = "translateY(-2px)"; }}
                              onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}
                            >{t}</span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* ── Experience & Education ── */}
      <section id="experience" style={{ position: "relative", zIndex: 1, padding: "100px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <FadeIn>
            <div style={{ textAlign: "center", marginBottom: 64 }}>
              <h2 className="section-title">Experience & Education</h2>
              <p className="section-sub">◈ My Journey ◈</p>
            </div>
          </FadeIn>
          <div style={{ display: "flex", gap: 60, flexWrap: "wrap" }}>
            {/* Experience */}
            <div style={{ flex: "1 1 340px" }}>
              <FadeIn delay={100}>
                <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 12, letterSpacing: 3, color: "#4a6070", marginBottom: 32 }}>WORK EXPERIENCE</h3>
                <div style={{ position: "relative" }}>
                  <div style={{ position: "absolute", left: 11, top: 12, bottom: 0, width: 1, background: "linear-gradient(to bottom, #00f5ff, #a855f722)" }} />
                  {EXPERIENCE.map((e, i) => (
                    <FadeIn key={e.role + e.company} delay={i * 120}>
                      <div style={{ display: "flex", gap: 20, marginBottom: 40 }}>
                        <div style={{ width: 22, height: 22, borderRadius: "50%", border: `2px solid ${e.color}`, background: `${e.color}22`, flexShrink: 0, marginTop: 2, boxShadow: `0 0 12px ${e.color}66` }} />
                        <div style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${e.color}22`, borderRadius: 14, padding: 22, flex: 1 }}>
                          <div style={{ fontSize: 10, color: e.color, letterSpacing: 2, fontFamily: "'Orbitron', sans-serif", marginBottom: 6 }}>{e.period}</div>
                          <h4 style={{ color: "#fff", fontFamily: "'Orbitron', sans-serif", fontSize: 14, marginBottom: 4 }}>{e.role}</h4>
                          <div style={{ color: "#a855f7", fontSize: 12, marginBottom: 12, letterSpacing: 1 }}>{e.company}</div>
                          <ul style={{ paddingLeft: 16 }}>
                            {e.points.map(pt => <li key={pt} style={{ color: "#8899aa", fontSize: 13, lineHeight: 1.7, marginBottom: 6 }}>{pt}</li>)}
                          </ul>
                        </div>
                      </div>
                    </FadeIn>
                  ))}
                </div>
              </FadeIn>
            </div>
            {/* Education */}
            <div style={{ flex: "1 1 300px" }}>
              <FadeIn delay={200}>
                <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 12, letterSpacing: 3, color: "#4a6070", marginBottom: 32 }}>EDUCATION</h3>
                <div style={{ position: "relative" }}>
                  <div style={{ position: "absolute", left: 11, top: 12, bottom: 0, width: 1, background: "linear-gradient(to bottom, #a855f7, #00f5ff22)" }} />
                  {EDUCATION.map((e, i) => (
                    <FadeIn key={e.degree} delay={i * 150}>
                      <div style={{ display: "flex", gap: 20, marginBottom: 40 }}>
                        <div style={{ width: 22, height: 22, borderRadius: "50%", border: `2px solid ${e.color}`, background: `${e.color}22`, flexShrink: 0, marginTop: 2, boxShadow: `0 0 12px ${e.color}66` }} />
                        <div style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${e.color}22`, borderRadius: 14, padding: 22, flex: 1 }}>
                          <div style={{ fontSize: 10, color: e.color, letterSpacing: 2, fontFamily: "'Orbitron', sans-serif", marginBottom: 6 }}>{e.period}</div>
                          <h4 style={{ color: "#fff", fontFamily: "'Orbitron', sans-serif", fontSize: 14, marginBottom: 4 }}>{e.degree}</h4>
                          <div style={{ color: "#10b981", fontSize: 12, marginBottom: 10, letterSpacing: 1 }}>{e.institute}</div>
                          <p style={{ color: "#8899aa", fontSize: 12, lineHeight: 1.6 }}>{e.detail}</p>
                        </div>
                      </div>
                    </FadeIn>
                  ))}
                </div>
                {/* CV download */}
                <a href={CV_LINK} download="Shees-Ikram-CV.pdf" style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "16px 24px", borderRadius: 12, marginTop: 8,
                  background: "linear-gradient(135deg, rgba(0,245,255,0.1), rgba(168,85,247,0.1))",
                  border: "1px solid rgba(0,245,255,0.2)",
                  color: "#00f5ff", textDecoration: "none", fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 2,
                  transition: "all 0.3s",
                }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 0 30px rgba(0,245,255,0.2)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}
                >
                  ↓ DOWNLOAD FULL RESUME
                </a>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* ── Projects ── */}
      <section id="projects" style={{ position: "relative", zIndex: 1, padding: "100px 24px", background: "rgba(0,0,0,0.3)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <FadeIn>
            <div style={{ textAlign: "center", marginBottom: 64 }}>
              <h2 className="section-title">Projects</h2>
              <p className="section-sub">◈ Hover Cards to Flip ◈</p>
            </div>
          </FadeIn>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 24 }}>
            {PROJECTS.map((p, i) => (
              <FadeIn key={p.title} delay={i * 80}>
                <ProjectCard p={p} />
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={300}>
            <div style={{ textAlign: "center", marginTop: 48 }}>
              <a href="https://github.com/Sheesikram" target="_blank" rel="noreferrer" className="glow-btn" style={{ background: "transparent", color: "#00f5ff", border: "1px solid #00f5ff44", display: "inline-block" }}>
                VIEW MORE ON GITHUB →
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── Contact ── */}
      <section id="contact" style={{ position: "relative", zIndex: 1, padding: "100px 24px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <FadeIn>
            <div style={{ textAlign: "center", marginBottom: 64 }}>
              <h2 className="section-title">Get In Touch</h2>
              <p className="section-sub">◈ Let's Build Something Together ◈</p>
            </div>
          </FadeIn>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20, marginBottom: 48 }}>
            {[
              { icon: "📍", label: "Location", val: "Faisalabad, Pakistan", href: null, color: "#00f5ff" },
              { icon: "📧", label: "Email", val: "sheesikram20@gmail.com", href: "mailto:sheesikram20@gmail.com", color: "#a855f7" },
              { icon: "📞", label: "Phone", val: "+92 323 4773767", href: "tel:+923234773767", color: "#10b981" },
              { icon: "🔗", label: "LinkedIn", val: "in/shees-ikram", href: "https://www.linkedin.com/in/shees-ikram/", color: "#f59e0b" },
            ].map(c => (
              <FadeIn key={c.label} delay={100}>
                <a href={c.href || "#"} target={c.href ? "_blank" : "_self"} rel="noreferrer" className="card-hover" style={{
                  display: "flex", flexDirection: "column", gap: 8,
                  padding: 24, borderRadius: 16,
                  background: `linear-gradient(135deg, ${c.color}0d, ${c.color}05)`,
                  border: `1px solid ${c.color}33`, textDecoration: "none",
                }}>
                  <div style={{ fontSize: 28 }}>{c.icon}</div>
                  <div style={{ fontSize: 10, color: c.color, letterSpacing: 2, fontFamily: "'Orbitron', sans-serif" }}>{c.label}</div>
                  <div style={{ fontSize: 13, color: "#c0cfe0", fontFamily: "'Rajdhani', sans-serif", fontWeight: 600 }}>{c.val}</div>
                </a>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={200}>
            <div style={{ textAlign: "center" }}>
              <a href="mailto:sheesikram20@gmail.com" className="glow-btn" style={{ background: "linear-gradient(135deg, #00f5ff, #a855f7)", color: "#000", fontSize: 13, boxShadow: "0 0 40px rgba(0,245,255,0.3)", padding: "16px 40px" }}>
                SEND ME A MESSAGE ✉️
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ position: "relative", zIndex: 1, borderTop: "1px solid rgba(0,245,255,0.1)", padding: "32px 24px", textAlign: "center" }}>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 20, fontWeight: 900, color: "#00f5ff", marginBottom: 12, textShadow: "0 0 20px #00f5ff44" }}>
          SI<span style={{ color: "#a855f7" }}>.</span>
        </div>
        <p style={{ fontSize: 12, color: "#2a3a4a", letterSpacing: 2 }}>
          © 2026 SHEES IKRAM · AI FULL STACK DEVELOPER · BUILT WITH ♥
        </p>
      </footer>
    </div>
  );
}
